import { motion } from "framer-motion";
import { ArrowUpRight, Sparkles, Sun, ShieldCheck, Gem } from "lucide-react";
import { PPF } from "../data";
import { useI18n } from "../i18n";

const FEATURE_ICONS = [Sparkles, Sun, ShieldCheck, Gem];

const Card = ({ item, i, t, onQuote }) => (
  <motion.div
    data-testid={`ppf-card-${item.id}`}
    initial={{ opacity: 0, y: 50 }}
    whileInView={{ opacity: 1, y: 0 }}
    viewport={{ once: true, margin: "-60px" }}
    transition={{ duration: 0.6, delay: i * 0.1, ease: [0.16, 1, 0.3, 1] }}
    className="group relative flex flex-col overflow-hidden rounded-2xl border border-white/10 bg-[#0F1115] hover:border-cyan-400/40 transition-colors duration-500"
  >
    <div className="relative overflow-hidden aspect-[16/11]">
      <img src={item.image} alt={t(`ppf.items.${item.id}.name`)} loading="lazy" className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" />
      <span className="absolute top-4 left-4 font-mono-accent text-[10px] uppercase tracking-[0.2em] bg-black/55 backdrop-blur px-3 py-1.5 rounded-full border border-cyan-400/30 text-cyan">
        {t(`ppf.items.${item.id}.tag`)}
      </span>
    </div>
    <div className="p-6 flex flex-col flex-1">
      <h3 className="font-display font-600 uppercase tracking-tight text-lg md:text-xl group-hover:text-cyan transition-colors duration-300">
        {t(`ppf.items.${item.id}.name`)}
      </h3>
      <p className="text-neutral-400 text-sm font-light mt-2 flex-1">{t(`ppf.items.${item.id}.desc`)}</p>
      <button
        data-testid={`ppf-cta-${item.id}`}
        onClick={onQuote}
        className="mt-5 inline-flex items-center justify-between gap-2 text-sm font-medium text-cyan border border-cyan-400/30 rounded-xl px-4 py-3 hover:bg-cyan-400 hover:text-black transition-colors duration-300"
      >
        {t("ppf.cta")}
        <ArrowUpRight size={16} />
      </button>
    </div>
  </motion.div>
);

export const PPFSection = ({ onQuote }) => {
  const { t } = useI18n();
  const features = t("ppf.features");

  return (
    <section id="ppf" data-testid="ppf-section" className="relative py-28 md:py-36 px-6 md:px-10">
      <div className="max-w-[1400px] mx-auto">
        <div className="flex flex-col md:flex-row md:items-end justify-between gap-6 mb-10">
          <div>
            <span className="font-mono-accent text-xs uppercase tracking-[0.3em] text-cyan">{t("ppf.tag")}</span>
            <h2 className="font-display font-700 uppercase tracking-tighter text-4xl md:text-6xl mt-4 leading-none">
              {t("ppf.title1")} <span className="text-cyan neon-text-glow">{t("ppf.title2")}</span>
            </h2>
          </div>
          <p className="max-w-sm text-neutral-400 font-light">{t("ppf.subtitle")}</p>
        </div>

        <div className="flex flex-wrap gap-3 mb-12">
          {features.map((f, i) => {
            const Icon = FEATURE_ICONS[i % FEATURE_ICONS.length];
            return (
              <div key={i} data-testid={`ppf-feature-${i}`} className="inline-flex items-center gap-2 rounded-full border border-white/10 bg-white/[0.02] px-4 py-2">
                <Icon size={15} className="text-cyan" />
                <span className="font-mono-accent text-xs uppercase tracking-[0.15em] text-neutral-300">{f}</span>
              </div>
            );
          })}
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
          {PPF.map((item, i) => (
            <Card key={item.id} item={item} i={i} t={t} onQuote={onQuote} />
          ))}
        </div>
      </div>
    </section>
  );
};
